import { api, ApiError, unwrap } from '@/core/http';
import { filenameFromDisposition } from '../domain/results';
import type {
  EntrySettingsBody,
  FlowCreateBody,
  FlowDocument,
  FlowSaveBody,
  PreviewBody,
  SessionStatus,
} from '../domain/types';

const flow = (flow_id: string) => ({ params: { path: { flow_id } } });

export interface ResultsParams {
  status?: SessionStatus;
  limit: number;
  offset: number;
}

/** As rotas do builder — todas pedem `centuria.fluxos` (os resultados são dados pessoais). */
export const flowsApi = {
  list: () => unwrap(api.GET('/v1/flows')),
  get: (id: string) => unwrap(api.GET('/v1/flows/{flow_id}', flow(id))),
  create: (body: FlowCreateBody) => unwrap(api.POST('/v1/flows', { body })),
  save: (id: string, body: FlowSaveBody) => unwrap(api.PUT('/v1/flows/{flow_id}', { ...flow(id), body })),
  remove: (id: string) => unwrap(api.DELETE('/v1/flows/{flow_id}', flow(id))),
  publish: (id: string) => unwrap(api.POST('/v1/flows/{flow_id}/publish', flow(id))),
  unpublish: (id: string) => unwrap(api.POST('/v1/flows/{flow_id}/unpublish', flow(id))),

  validate: (definition: FlowDocument) => unwrap(api.POST('/v1/flows/validate', { body: { definition } })),
  preview: (body: PreviewBody) => unwrap(api.POST('/v1/flows/preview', { body })),
  lessons: (q: string) => unwrap(api.GET('/v1/flows/lessons', { params: { query: { q } } })),

  settings: () => unwrap(api.GET('/v1/flows/settings')),
  saveSettings: (body: EntrySettingsBody) => unwrap(api.PUT('/v1/flows/settings', { body })),

  results: (id: string, { status, limit, offset }: ResultsParams) =>
    unwrap(
      api.GET('/v1/flows/{flow_id}/results', {
        params: { path: { flow_id: id }, query: { status, limit, offset } },
      }),
    ),

  /**
   * A planilha (CSV) dos resultados, pelo mesmo cliente autenticado, como
   * arquivo — nunca por um link aberto, que levaria o token na URL.
   */
  exportResults: async (id: string): Promise<{ blob: Blob; filename: string }> => {
    let result;
    try {
      result = await api.GET('/v1/flows/{flow_id}/results/export', { ...flow(id), parseAs: 'blob' });
    } catch {
      throw ApiError.from(0, null);
    }
    if (!result.response.ok || !(result.data instanceof Blob))
      throw ApiError.from(result.response.status, result.error);
    const filename = filenameFromDisposition(
      result.response.headers.get('Content-Disposition'),
      'resultados.csv',
    );
    return { blob: result.data, filename };
  },
};
